// Space partition (SKTICK-INTSPACE-001) — API contract types.
// Mirrors the `sk-tickets-api` /v1/spaces serializers exactly.

export type SpaceType = 'PUBLIC' | 'INTERNAL';
export type SpaceRole = 'ADMIN' | 'AGENT' | 'VIEWER';
export type MembershipEventType = 'CREATE' | 'GRANT' | 'REVOKE' | 'ROLE_CHANGE' | 'DEACTIVATE';

/** A PUBLIC/INTERNAL partition. Type + requesterSource are immutable after creation. */
export interface ApiSpace {
  id: string;
  type: SpaceType;
  name: string;
  /** Stable slug, lowercase alnum/dashes (e.g. "hr"). Rendered uppercased in the UI. */
  key: string;
  /** Derived from `type` server-side: INTERNAL → "sk-people", PUBLIC → "sk-crm". */
  requesterSource: string;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  deactivatedAt: string | null;
}

/** An active staff grant. The ONLY staff-access path — no org-chart inheritance. */
export interface ApiSpaceMember {
  spaceId: string;
  platformUserId: string;
  role: SpaceRole;
  isActive: boolean;
  grantedBy: string;
  grantedAt: string;
  revokedBy: string | null;
  revokedAt: string | null;
}

/** Append-only membership audit event (never mutated, only appended). */
export interface ApiMembershipEvent {
  id: string;
  spaceId: string;
  platformUserId: string;
  eventType: MembershipEventType;
  role: SpaceRole | null;
  actorId: string;
  at: string;
}

export interface CreateSpaceInput {
  name: string;
  /** Lowercase alnum/dashes. Chosen once and locked. */
  key: string;
  type: SpaceType;
}

export interface GrantMemberInput {
  platformUserId: string;
  role: SpaceRole;
  /** Idempotency key — membership writes are safe to retry. */
  idempotencyKey?: string;
}

/** Standard list envelope used by the spaces endpoints. */
export interface ListEnvelope<T> {
  data: T[];
  count: number;
}

export const SPACE_ROLE_LABELS: Record<SpaceRole, string> = {
  ADMIN: 'Admin',
  AGENT: 'Agent',
  VIEWER: 'Viewer',
};

/** Human label for a derived requester source. */
export function requesterSourceLabel(source: string): string {
  const s = (source || '').toLowerCase();
  if (s === 'sk-people' || s === 'sk people') return 'SK People';
  if (s === 'sk-crm' || s === 'sk crm') return 'SK CRM';
  return source;
}
